import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { IconButton } from '../Button';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../Command';

export type UniversalSearchItem = {
  value: string;
  label: string;
  onSelect: () => void;
};

export type UniversalSearchGroup = {
  heading: string;
  items: UniversalSearchItem[];
};

export type UniversalSearchEmptyAction = {
  label: string;
  icon?: React.ReactNode;
  onSelect: () => void;
  'data-testid'?: string;
};

export type UniversalSearchProps = {
  getItems: (searchTerm: string) => Promise<UniversalSearchGroup[]>;
  triggerLabel?: string;
  placeholder?: string;
  minSearchLength?: number;
  emptyHint?: React.ReactNode;
  noResultsText?: string;
  /** Listed as an option, not a Tab stop: arrows reach it and Tab leaves the search, per the combobox pattern. */
  emptyAction?: UniversalSearchEmptyAction;
  onOpen?: () => void;
  className?: string;
  ref?: React.Ref<HTMLButtonElement>;
  'data-testid'?: string;
};

const EXPANDED_WIDTH = 'w-[240px]';
// Slightly under the 300ms width transition so the fade overlaps its tail instead of waiting for it.
const DROPDOWN_DELAY_MS = 200;
// Rapid keystrokes collapse into one request for the settled term.
const SEARCH_DEBOUNCE_MS = 200;
const NO_HIGHLIGHT = '__none__';
const EMPTY_ACTION_VALUE = '__empty-action__';

function UniversalSearch({
  getItems,
  triggerLabel = 'Search',
  placeholder = 'Search',
  minSearchLength = 1,
  emptyHint,
  noResultsText = 'No results found.',
  emptyAction,
  onOpen,
  className,
  ref,
  'data-testid': testId,
}: UniversalSearchProps) {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [groups, setGroups] = React.useState<UniversalSearchGroup[]>([]);
  const [resolvedTerm, setResolvedTerm] = React.useState('');
  // A value matching no option stops cmdk auto-highlighting the empty-state action, so Enter in an empty field does nothing.
  const [highlight, setHighlight] = React.useState(NO_HIGHLIGHT);
  // The dropdowns fade in as the width transition ends, so they never sit beside a half-grown input.
  const [expanded, setExpanded] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const focusTriggerAfterClose = React.useRef(false);
  const skipFocusOpen = React.useRef(false);
  const requestId = React.useRef(0);
  const getItemsRef = React.useRef(getItems);
  React.useEffect(() => {
    getItemsRef.current = getItems;
  }, [getItems]);

  React.useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement, []);

  React.useEffect(() => {
    if (!open) return;
    setHighlight(searchTerm ? '' : NO_HIGHLIGHT);
    const id = ++requestId.current;
    if (searchTerm.length < minSearchLength) {
      setGroups([]);
      setResolvedTerm(searchTerm);
      return;
    }
    const timer = setTimeout(() => {
      getItemsRef
        .current(searchTerm)
        .then((result) => {
          if (id !== requestId.current) return;
          setGroups(result);
          setResolvedTerm(searchTerm);
        })
        .catch(() => {
          if (id !== requestId.current) return;
          setGroups([]);
          setResolvedTerm(searchTerm);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, searchTerm, minSearchLength]);

  React.useEffect(() => {
    if (open || !focusTriggerAfterClose.current) return;
    focusTriggerAfterClose.current = false;
    skipFocusOpen.current = true;
    triggerRef.current?.focus();
    skipFocusOpen.current = false;
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      setExpanded(false);
      return;
    }
    const timer = setTimeout(() => setExpanded(true), DROPDOWN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [open]);

  const openSearch = () => {
    if (open) return;
    setOpen(true);
    onOpen?.();
  };

  const closeSearch = (refocusTrigger: boolean) => {
    if (!open) return;
    requestId.current++;
    focusTriggerAfterClose.current = refocusTrigger;
    setOpen(false);
    setSearchTerm('');
    setGroups([]);
    setResolvedTerm('');
    setHighlight(NO_HIGHLIGHT);
  };

  const handleSelect = (item: UniversalSearchItem) => {
    item.onSelect();
    closeSearch(true);
  };

  // Keeps focus in the input through a click, so the blur handler never closes mid-click.
  const keepInputFocus = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
  };

  const hasResults = groups.some((group) => group.items.length > 0);
  const hasTerm = searchTerm.length > 0;
  const searchStarted = searchTerm.length >= minSearchLength && hasTerm;
  const showNoResults = searchStarted && resolvedTerm === searchTerm && !hasResults;
  const showEmptyState = open && !hasResults && !showNoResults && Boolean(emptyHint || emptyAction);
  const showEmptyAction = showEmptyState && !hasTerm && Boolean(emptyAction);
  const derivedTestId = (suffix: string) => (testId ? `${testId}-${suffix}` : undefined);
  // Fixed at the final width so nothing reflows while the input is still widening.
  const dropdownClass = cn(
    'absolute right-0 top-6 overflow-hidden rounded-t-none rounded-b border border-t-0 border-blue-50 bg-white shadow-md',
    EXPANDED_WIDTH,
    'transition-opacity duration-150',
    expanded ? 'opacity-100' : 'opacity-0',
  );

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: blur and Escape are delegated from the trigger and input inside
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: same delegation; the container itself is never a target
    <div
      ref={rootRef}
      data-slot="universal-search"
      // Anchored right so the box grows leftward from the icon, away from the controls beside it.
      className={cn(
        'relative flex h-6 items-center transition-[width] duration-300 ease-in-out',
        open ? EXPANDED_WIDTH : 'w-6',
        className,
      )}
      onBlur={(event) => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) closeSearch(false);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault();
          closeSearch(true);
        }
      }}
    >
      <IconButton
        ref={triggerRef}
        icon="magnifyingGlass"
        variant="secondary"
        size="icon"
        aria-label={triggerLabel}
        data-slot="universal-search-trigger"
        data-state={open ? 'open' : 'closed'}
        data-testid={testId}
        tabIndex={open ? -1 : 0}
        aria-hidden={open || undefined}
        onFocus={() => {
          if (skipFocusOpen.current) return;
          openSearch();
        }}
        onClick={openSearch}
        className={cn(
          'absolute right-0 top-0 transition-opacity duration-200 ease-in-out',
          open && 'pointer-events-none opacity-0',
        )}
      />

      {open && (
        <Command
          label={triggerLabel}
          shouldFilter={false}
          loop
          value={highlight}
          onValueChange={setHighlight}
          // Spans the root so the input widens with the width transition instead of appearing full size.
          className="absolute inset-x-0 top-0 z-50 h-auto overflow-visible"
          data-testid={derivedTestId('popup')}
        >
          <CommandInput
            autoFocus
            value={searchTerm}
            onValueChange={setSearchTerm}
            placeholder={placeholder}
            data-testid={derivedTestId('input')}
            borderColor="border-blue-50"
            wrapperClassName="h-6 rounded rounded-b-none border bg-white"
            className="text-grey-80"
          />
          {(hasResults || showNoResults) && (
            <div className={dropdownClass}>
              {showNoResults ? (
                <div data-testid={derivedTestId('no-results')} className="py-6 text-center text-xs text-grey-80">
                  {noResultsText}
                </div>
              ) : (
                <CommandList
                  data-testid={derivedTestId('results')}
                  className="max-h-[60vh]"
                  onMouseDown={keepInputFocus}
                >
                  {groups.map((group) => (
                    <CommandGroup key={group.heading} heading={group.heading}>
                      {group.items.map((item) => (
                        <CommandItem
                          key={item.value}
                          value={item.value}
                          onSelect={() => handleSelect(item)}
                          data-testid={derivedTestId(`option-${item.value}`)}
                          className="cursor-pointer"
                        >
                          <span className="truncate">{item.label}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
              )}
            </div>
          )}
          {showEmptyState && (
            <div data-testid={derivedTestId('empty')} className={dropdownClass}>
              {emptyHint && <div className="px-2 py-2 text-xs text-grey-80">{emptyHint}</div>}
              {showEmptyAction && emptyAction && (
                <CommandList className="border-t border-grey-30" onMouseDown={keepInputFocus}>
                  <CommandItem
                    value={EMPTY_ACTION_VALUE}
                    onSelect={emptyAction.onSelect}
                    data-testid={emptyAction['data-testid'] ?? derivedTestId('empty-action')}
                    className="cursor-pointer text-grey-90"
                  >
                    {emptyAction.icon}
                    {emptyAction.label}
                  </CommandItem>
                </CommandList>
              )}
            </div>
          )}
        </Command>
      )}
    </div>
  );
}

export { UniversalSearch };
